import { translate } from 'src/plugins/i18n'
import type {
  DynamicRuleInterface,
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { FEATURE_IDS } from 'src/tpbp-core/feature-flag/featureFlags.const'
import { hasFeature } from 'src/tpbp-core/feature-flag/featureFlags.utils'
import { FIELD_TYPES } from 'src/tpbp-core/models/fields/field-set.const'
import { FIELD_RULES } from 'src/tpbp-core/models/fields/fieldRules.const'
import { TEXT_INPUT_FORMATS } from 'src/tpbp-core/models/fields/text-input.const'
import type { ContractLineModel } from 'src/tpbp-json-api-handler/models/models'
import { checkChangeBeforeValidate } from '../mapping.utils'
import {
  computeAmountMinControl,
  computeDepositIsReadOnly,
  computeDepositMaxControl,
  computeDepositMinControl,
  computeMonthlyAmountIsReadOnly,
  computeMonthlyAmountMaxControl,
  computeMonthlyAmountMinControl,
  computeWithdrawalIsReadOnly,
  computeWithdrawalMaxControl,
} from './contractLine.model.utils'

const readOnlyRules: DynamicRuleInterface[] = [
  {
    expression: ({ isReadOnly }: ContractLineModel) => isReadOnly,
    type: FIELD_RULES.isReadOnly,
  },
  {
    expression: () => true,
    type: FIELD_RULES.isOptional,
  },
]

const monthlyAmountReadOnlyRules: DynamicRuleInterface[] = [
  {
    expression: computeMonthlyAmountIsReadOnly,
    type: FIELD_RULES.isReadOnly,
  },
  {
    expression: () => true,
    type: FIELD_RULES.isOptional,
  },
]

export const contractLineFields: FieldInterface[] = [
  {
    key: 'isin',
    originKey: 'isin',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    dependentFieldKeys: ['nameOrIsin'],
  },
  {
    key: 'name',
    originKey: 'name',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    dependentFieldKeys: ['nameOrIsin'],
  },
  {
    key: 'nameOrIsin',
    originKey: 'nameOrIsin',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    isPseudoAttribute: true,
    expression: ({ name, isin }: ContractLineModel) => name || isin,
  },
  {
    key: 'amountInitial',
    originKey: 'amount_initial',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    dynamicRules: readOnlyRules,
    dependentFieldKeys: ['amount', 'weight', 'initialWeight'],
  },
  {
    key: 'deposit',
    originKey: 'deposit',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    dynamicRules: [
      {
        expression: computeDepositIsReadOnly,
        type: FIELD_RULES.isReadOnly,
      },
      {
        expression: () => true,
        type: FIELD_RULES.isOptional,
      },
    ],
    dependentFieldKeys: ['amount', 'weight', 'initialWeight'],
    controls: {
      max: ({
        amountInitial,
        maxInitialPayment,
        exceptionalPaymentMax,
        deposit,
        $hasChanged,
      }: ContractLineModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          computeDepositMaxControl(
            amountInitial,
            maxInitialPayment,
            exceptionalPaymentMax,
            deposit,
          ),
        ),
      min: ({
        amountInitial,
        minInitialPayment,
        exceptionalPaymentMin,
        deposit,
        $hasChanged,
      }: ContractLineModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          computeDepositMinControl(
            amountInitial,
            minInitialPayment,
            exceptionalPaymentMin,
            deposit,
          ),
        ),
    },
  },
  {
    key: 'esg',
    originKey: 'esg',
    type: FIELD_TYPES.object,
    objectType: 'EsgCriterias',
    objectTypePath: 'src/services/people/peopleApiServiceRiskLevels.types',
  },
  {
    key: 'withdrawal',
    originKey: 'withdrawal',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    dynamicRules: [
      {
        type: FIELD_RULES.isReadOnly,
        expression: computeWithdrawalIsReadOnly,
      },
      {
        type: FIELD_RULES.isOptional,
        expression: ({ amountInitial }: ContractLineModel) => amountInitial > 0,
      },
    ],
    dependentFieldKeys: ['amount', 'weight', 'initialWeight'],
    controls: {
      max: ({
        amountInitial,
        withdrawal,
        balanceMin,
        $hasChanged,
      }: ContractLineModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          computeWithdrawalMaxControl(amountInitial, withdrawal, balanceMin),
        ),
    },
    messages: {
      max: (max: number) =>
        translate('models.contract_lines.withdrawal.messages.max', { max }),
    },
  },
  {
    key: 'amount',
    originKey: 'amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    rule: FIELD_RULES.isReadOnly,
    isPseudoAttribute: true,
    expression: ({ amountInitial, deposit, withdrawal }) =>
      (amountInitial || 0) + (deposit || 0) - (withdrawal || 0),
    controls: {
      min: ({ amount, balanceMin, $hasChanged }: ContractLineModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          computeAmountMinControl(amount, balanceMin),
        ),
    },
  },
  {
    key: 'exceptionalPaymentMin',
    originKey: 'exceptional_payment_min',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'pledgeAccount',
    originKey: 'pledgeAccount',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
    isPseudoAttribute: true,
  },
  {
    key: 'productGroup',
    originKey: 'product_group',
    type: FIELD_TYPES.string,
    isPseudoAttribute: true,
  },
  {
    key: 'isHidden',
    originKey: 'is_hidden',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'exceptionalPaymentMax',
    originKey: 'exceptional_payment_max',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'initialWeight',
    originKey: 'initialWeight',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.percentage,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'weight',
    originKey: 'weight',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.percentage,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'monthlyAmountInitial',
    originKey: 'monthly_amount_initial',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'monthlyAmount',
    originKey: 'monthly_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    dynamicRules: monthlyAmountReadOnlyRules,
    controls: {
      max: ({
        monthlyAmount,
        maxScheduledPayment,
        monthlyAmountInitial,
        isEsgCriteriaCompliant,
        $hasChanged,
      }: ContractLineModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          computeMonthlyAmountMaxControl(
            monthlyAmount,
            maxScheduledPayment,
            monthlyAmountInitial,
            hasFeature(FEATURE_IDS.dmpEsg),
            isEsgCriteriaCompliant,
          ),
        ),
      min: ({
        monthlyAmount,
        minScheduledPayment,
        monthlyAmountInitial,
        pledgeAccount,
        $hasChanged,
      }: ContractLineModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          computeMonthlyAmountMinControl(
            monthlyAmount,
            minScheduledPayment,
            monthlyAmountInitial,
            pledgeAccount,
          ),
        ),
    },
  },
  {
    key: 'srri',
    originKey: 'srri',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'pdfUrl',
    originKey: 'pdf_url',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'monthlyBuyoutInitial',
    originKey: 'monthly_buyout_initial',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
    format: TEXT_INPUT_FORMATS.currency2,
  },
  {
    key: 'minInitialPayment',
    originKey: 'min_initial_payment',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'maxInitialPayment',
    originKey: 'max_initial_payment',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'minScheduledPayment',
    originKey: 'min_scheduled_payment',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'maxScheduledPayment',
    originKey: 'max_scheduled_payment',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'hasDicDici',
    originKey: 'has_dic_dici',
    type: FIELD_TYPES.boolean,
  },
  {
    key: 'isSelectable',
    originKey: 'isSelectable',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'kind',
    originKey: 'kind',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'managementType',
    originKey: 'management_type',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    enumType: 'ApiPortfolioManagementModes',
    enumTypePath: 'src/tpbp-json-api-handler/models/contract/contract.types.ts',
  },
  {
    key: 'contractManagementType',
    originKey: 'contractManagementType',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    enumType: 'ApiPortfolioManagementModes',
    enumTypePath: 'src/tpbp-json-api-handler/models/contract/contract.types.ts',
    isPseudoAttribute: true,
  },
  {
    key: 'contractManagementTypeInitial',
    originKey: 'contractManagementTypeInitial',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    enumType: 'ApiPortfolioManagementModes',
    enumTypePath: 'src/tpbp-json-api-handler/models/contract/contract.types.ts',
    isPseudoAttribute: true,
  },
  {
    key: 'type',
    originKey: 'type',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    enumType: 'ContratLineType',
    enumTypePath:
      'src/tpbp-json-api-handler/models/contract/contract-line.types',
  },
  {
    key: 'isNew',
    originKey: 'is_new',
    type: FIELD_TYPES.boolean,
    default: false,
  },
  {
    key: 'isReadOnly',
    originKey: 'isReadOnly',
    type: FIELD_TYPES.boolean,
    isPseudoAttribute: true,
  },
  {
    key: 'isForDeposit',
    originKey: 'is_for_deposit',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'isEsgCriteriaCompliant',
    originKey: 'is_esg_criteria_compliant',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'balanceMin',
    originKey: 'balance_min',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'isMonthlyPaymentPossible',
    originKey: 'is_monthly_payment_possible',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'isArbitrable',
    originKey: 'is_arbitrable',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'composition',
    originKey: 'composition',
    type: FIELD_TYPES.object,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'isEligibleWithMandats',
    originKey: 'is_eligible_with_mandats',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'qce',
    originKey: 'qce',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'investmentHorizon',
    originKey: 'investment_horizon',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
  },
]

export const contractLineRelations: RelationInterface[] = []
